import React from 'react';
import { motion } from 'framer-motion';
import { SUPPORT_LINKS } from '../../config/appConfig';
import { useLanguage } from '../../contexts/LanguageContext';

const MethodMaintenanceModal = ({ onClose }) => {
    const { t } = useLanguage();

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { type: 'spring', damping: 25, stiffness: 200 }
        }
    };

    return (
        <div className="relative">
            {/* Header */}
            <motion.div
                initial="hidden"
                animate="visible"
                variants={itemVariants}
                className="flex items-center justify-between mb-8"
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-accent-yellow/10 flex items-center justify-center">
                        <i className="fas fa-wrench text-accent-yellow"></i>
                    </div>
                    <h2 className="text-xl font-bold text-white">
                        {t('maintenanceMode')}
                    </h2>
                </div>
                <button
                    onClick={onClose}
                    className="w-8 h-8 rounded-lg hover:bg-white/5 transition-colors flex items-center justify-center group"
                    aria-label="Close"
                >
                    <i className="fas fa-times text-gray-500 group-hover:text-white transition-colors"></i>
                </button>
            </motion.div>

            {/* Content */}
            <motion.div
                initial="hidden"
                animate="visible"
                className="space-y-8"
                variants={{
                    visible: { transition: { staggerChildren: 0.1 } }
                }}
            >
                {/* Main Icon & Text */}
                <motion.div variants={itemVariants} className="text-center space-y-4">
                    <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-accent-yellow/20 to-accent-orange/20 items-center justify-center border border-accent-yellow/20">
                        <i className="fas fa-tools text-accent-yellow text-2xl"></i>
                    </div>

                    <div>
                        <h3 className="text-xl font-bold text-white mb-2">
                            {t('methodAvailableSoon')}
                        </h3>
                        <p className="text-sm text-gray-400 max-w-xs mx-auto leading-relaxed">
                            {t('maintenanceDescription')}
                        </p>
                    </div>
                </motion.div>

                {/* Status Grid */}
                <motion.div variants={itemVariants} className="grid grid-cols-2 gap-3">
                    <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 text-center transition-colors hover:border-white/10 uppercase tracking-tighter">
                        <p className="text-[10px] text-gray-500 font-bold mb-1">{t('expected')}</p>
                        <p className="text-sm font-semibold text-white">{t('thisMonth')}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 text-center transition-colors hover:border-white/10 uppercase tracking-tighter">
                        <p className="text-[10px] text-gray-500 font-bold mb-1">{t('status')}</p>
                        <p className="text-sm font-semibold text-accent-green">{t('updating')}</p>
                    </div>
                </motion.div>

                {/* Socials */}
                <motion.div variants={itemVariants} className="space-y-4 pt-2">
                    <div className="flex items-center gap-3">
                        <div className="h-px flex-1 bg-white/5"></div>
                        <span className="text-[10px] text-gray-500 uppercase font-bold tracking-widest">{t('stayUpdated')}</span>
                        <div className="h-px flex-1 bg-white/5"></div>
                    </div>
                    <div className="flex gap-3">
                        <a
                            href={SUPPORT_LINKS.discord}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 flex items-center justify-center gap-2 bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 rounded-xl py-3 transition-colors group"
                        >
                            <i className="fab fa-discord text-gray-400 group-hover:text-[#5865F2] transition-colors"></i>
                            <span className="text-sm font-medium text-gray-300">Discord</span>
                        </a>
                        <a
                            href={SUPPORT_LINKS.telegram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 flex items-center justify-center gap-2 bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 rounded-xl py-3 transition-colors group"
                        >
                            <i className="fab fa-telegram text-gray-400 group-hover:text-[#0088cc] transition-colors"></i>
                            <span className="text-sm font-medium text-gray-300">Telegram</span>
                        </a>
                    </div>
                </motion.div>

                {/* CTA Button */}
                <motion.div variants={itemVariants} className="pt-2">
                    <motion.button
                        whileHover={{ y: -2 }}
                        whileTap={{ y: 0 }}
                        onClick={onClose}
                        className="w-full py-4 bg-gradient-to-r from-accent-yellow to-accent-orange text-dark-800 font-bold rounded-xl shadow-lg shadow-accent-yellow/5 hover:shadow-accent-yellow/10 transition-all uppercase tracking-wider text-xs"
                    >
                        {t('understood')}
                    </motion.button>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default MethodMaintenanceModal;